import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend } from "chart.js";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Skeleton } from "../../ui/skeleton";
import { 
  DatabaseIcon,
  Loader2Icon,
  InfoIcon,
  TrashIcon,
  PercentIcon,
  FileTextIcon
} from "lucide-react";
import { cn } from "../../../lib/utils";

// Register Chart.js components
ChartJS.register(ArcElement, ChartTooltip, Legend);

// Dataset item type for drag and drop
const DATASET_ITEM_TYPE = "dataset";

// Define interfaces for type safety
interface Dataset {
  id: number;
  name: string;
  description?: string;
  request_count: number;
}

interface SelectedDataset {
  dataset: Dataset;
  percentage: number;
  sampleType: "request" | "session";
  samplePercentage: number;
}

// Helper function to calculate sampled request count
const calculateSampledRequestCount = (dataset: Dataset, samplePercentage: number): number => {
  return Math.round((dataset.request_count * samplePercentage) / 100);
};

// Helper function to format request count with asterisk for session sampling
const formatSampledRequestCount = (dataset: SelectedDataset): string => {
  const count = calculateSampledRequestCount(dataset.dataset, dataset.samplePercentage);
  return dataset.sampleType === "session" ? `${count}*` : `${count}`;
};

// Check if any dataset in the composition uses session sampling
const hasSessionSampling = (datasets: SelectedDataset[]): boolean => {
  return datasets.some(dataset => dataset.sampleType === "session");
};

// Format total request count with asterisk if any dataset uses session sampling
const formatTotalRequestCount = (datasets: SelectedDataset[]): string => {
  const total = datasets.reduce((sum, item) => sum + calculateSampledRequestCount(item.dataset, item.samplePercentage), 0);
  return hasSessionSampling(datasets) ? `${total}*` : `${total}`;
};

// Dataset Card Component
interface DatasetCardProps {
  dataset: Dataset;
  isInComposition: boolean;
}

const DatasetCard: React.FC<DatasetCardProps> = ({ dataset, isInComposition }) => {
  const [{ isDragging }, drag] = useDrag({
    type: DATASET_ITEM_TYPE,
    item: dataset,
    canDrag: !isInComposition,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const dragRef = useCallback((node: HTMLDivElement | null) => {
    drag(node);
  }, [drag]);

  return (
    <div
      ref={dragRef}
      className={cn(
        "p-3 mb-2 rounded border transition-all duration-200",
        isDragging && "opacity-50",
        isInComposition 
          ? "bg-muted border-border cursor-default" 
          : "bg-card border-border cursor-move hover:border-maitai-green/50"
      )}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="text-sm font-gc-med text-foreground">{dataset.name}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {dataset.request_count.toLocaleString()} requests
          </div>
        </div>
        {isInComposition && (
          <div className="ml-2">
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
              {dataset.request_count.toLocaleString()}
            </span>
          </div>
        )}
      </div>
      <div className="text-xs text-muted-foreground mt-2 line-clamp-2">
        {dataset.description || "No description"}
      </div>
    </div>
  );
};

// Composition Builder Component
interface CompositionBuilderProps {
  datasets: Dataset[];
  selectedDatasets: SelectedDataset[];
  onAddDataset: (dataset: Dataset) => void;
  onRemoveDataset: (datasetId: number) => void;
  onConfigureDataset: (dataset: SelectedDataset) => void;
}

const CompositionBuilder: React.FC<CompositionBuilderProps> = ({
  datasets,
  selectedDatasets,
  onAddDataset,
  onRemoveDataset,
  onConfigureDataset,
}) => {
  const [{ isOver }, drop] = useDrop({
    accept: DATASET_ITEM_TYPE,
    drop: (item: Dataset) => {
      console.log("Drop item:", item);
      try {
        if (item && item.id) {
          onAddDataset(item);
        } else {
          console.error("Invalid drop item:", item);
        }
      } catch (error) {
        console.error("Error in drop handler:", error);
      }
      return { name: "CompositionBuilder" };
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  const dropRef = useCallback((node: HTMLDivElement | null) => {
    drop(node);
  }, [drop]);

  return (
    <div
      ref={dropRef}
      className={cn(
        "min-h-[300px] p-4 rounded-lg border-2 border-dashed transition-all duration-300",
        isOver 
          ? "border-maitai-green bg-maitai-green/5" 
          : "border-border bg-muted/20"
      )}
    >
      <div className="text-center mb-4">
        <h3 className="text-lg font-gc-med text-foreground flex items-center justify-center gap-2">
          <DatabaseIcon className="w-5 h-5 text-maitai-green" />
          Drop Datasets Here
          <div className="group relative">
            <InfoIcon className="w-4 h-4 text-muted-foreground cursor-help" />
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              Percentages are automatically calculated based on the number of requests in each dataset
            </div>
          </div>
        </h3>
      </div>

      {selectedDatasets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="space-y-3">
            <FileTextIcon className="w-12 h-12 text-muted-foreground mx-auto" />
            <h3 className="text-lg font-gc-med text-foreground">
              No datasets selected
            </h3>
            <p className="text-muted-foreground max-w-md">
              Drag and drop datasets here to build your composition
            </p>
          </div>
        </div>
      ) : (
        <div className="max-h-[300px] overflow-y-auto space-y-2">
          {[...selectedDatasets]
          .sort((a, b) => 
            calculateSampledRequestCount(b.dataset, b.samplePercentage) - 
            calculateSampledRequestCount(a.dataset, a.samplePercentage)
          )
          .map((item) => (
            <div
              key={item.dataset.id}
              className="flex items-center justify-between p-3 rounded border border-border bg-card cursor-pointer hover:bg-accent/20 transition-colors"
              onClick={() => onConfigureDataset(item)}
            >
              <div className="flex-1">
                <div className="text-sm font-gc-med text-foreground">{item.dataset.name}</div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                    {item.percentage}%
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {item.dataset.request_count.toLocaleString()} requests
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Sampling: {item.samplePercentage}% by {item.sampleType}
                </div>
                <div className="text-xs text-muted-foreground">
                  After sampling: <span className="font-gc-med text-foreground">
                    {item.sampleType === "session" ? `${calculateSampledRequestCount(item.dataset, item.samplePercentage)}*` : calculateSampledRequestCount(item.dataset, item.samplePercentage)}
                  </span> requests
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveDataset(item.dataset.id);
                }}
              >
                <TrashIcon className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Percentage Visualizer Component
interface PercentageVisualizerProps {
  selectedDatasets: SelectedDataset[];
  hideLabel?: boolean;
}

const PercentageVisualizer: React.FC<PercentageVisualizerProps> = ({ selectedDatasets, hideLabel = false }) => {
  const [hiddenDatasets, setHiddenDatasets] = useState<Set<number>>(new Set());
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Detect theme changes
  useEffect(() => {
    const detectTheme = () => {
      if (typeof window !== "undefined") {
        const darkMode = document.documentElement.classList.contains("dark") || 
               window.matchMedia("(prefers-color-scheme: dark)").matches ||
               getComputedStyle(document.documentElement).getPropertyValue("--background").includes("0 0% 0%");
        setIsDarkMode(darkMode);
      }
    };

    // Initial detection
    detectTheme();

    // Listen for theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    mediaQuery.addEventListener("change", detectTheme);

    // Listen for class changes on document element
    const observer = new MutationObserver(detectTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"]
    });

    return () => {
      mediaQuery.removeEventListener("change", detectTheme);
      observer.disconnect();
    };
  }, []);

  // Theme-aware colors
  const textColor = isDarkMode ? "#ffffff" : "#000000";
  const mutedTextColor = isDarkMode ? "#a3a3a3" : "#737373";
  
  // Calculate percentages based on sampled request counts
  const sampledDatasets = useMemo(() => {
    if (selectedDatasets.length === 0) return [];
    
    // Calculate sampled request counts
    const datasetsWithSampledCounts = selectedDatasets.map(item => ({
      ...item,
      sampledCount: calculateSampledRequestCount(item.dataset, item.samplePercentage)
    }));
    
    // Calculate total sampled requests
    const totalSampledRequests = datasetsWithSampledCounts.reduce(
      (sum, item) => sum + item.sampledCount, 0
    );
    
    // Calculate new percentages based on sampled counts
    return datasetsWithSampledCounts.map(item => ({
      ...item,
      sampledPercentage: totalSampledRequests > 0 
        ? Math.round((item.sampledCount / totalSampledRequests) * 100)
        : 0
    }));
  }, [selectedDatasets]);
  
  // Adjust percentages to ensure they sum to 100%
  const adjustedSampledDatasets = useMemo(() => {
    if (sampledDatasets.length === 0) return [];
    
    const totalPercentage = sampledDatasets.reduce(
      (sum, item) => sum + item.sampledPercentage, 0
    );
    
    if (totalPercentage === 100 || totalPercentage === 0) return sampledDatasets;
    
    // Adjust the dataset with the most sampled requests to make total 100%
    const result = [...sampledDatasets];
    const sortedByCount = [...result].sort((a, b) => b.sampledCount - a.sampledCount);
    sortedByCount[0].sampledPercentage += (100 - totalPercentage);
    
    return result;
  }, [sampledDatasets]);

  const chartData = useMemo(() => {
    // Sort datasets by sampled request count in descending order
    const sortedDatasets = [...adjustedSampledDatasets].sort(
      (a, b) => b.sampledCount - a.sampledCount
    );
    
    return {
      labels: sortedDatasets.map(item => item.dataset.name),
      datasets: [
        {
          data: sortedDatasets.map(item => item.sampledPercentage),
          backgroundColor: [
            "#1890ff",
            "#52c41a",
            "#faad14",
            "#f5222d",
            "#722ed1",
            "#13c2c2",
            "#fa8c16",
            "#eb2f96",
            "#a0d911",
            "#fadb14",
          ],
          borderColor: isDarkMode ? "#0a0a0a" : "#ffffff",
          borderWidth: 2,
        },
      ],
    };
  }, [adjustedSampledDatasets, isDarkMode]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: !hideLabel,
        position: "right" as const,
        onClick: (evt: any, legendItem: any, legend: any) => {
          const index = legendItem.index;
          const chart = legend.chart;
          const meta = chart.getDatasetMeta(0);
          
          // Toggle visibility
          meta.data[index].hidden = !meta.data[index].hidden;
          
          // Update hidden datasets state
          const newHiddenDatasets = new Set(hiddenDatasets);
          if (meta.data[index].hidden) {
            newHiddenDatasets.add(index);
          } else {
            newHiddenDatasets.delete(index);
          }
          setHiddenDatasets(newHiddenDatasets);
          
          chart.update();
        },
        labels: {
          color: textColor,
          font: {
            family: "'Greycliff CF', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            size: 12,
          },
          generateLabels: function(chart: any) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label: string, i: number) => {
                const dataset = data.datasets[0];
                const value = dataset.data[i];
                const backgroundColor = dataset.backgroundColor[i];
                const isHidden = hiddenDatasets.has(i);
                
                return {
                  text: `${label}: ${value}%`,
                  fillStyle: isHidden ? mutedTextColor : backgroundColor,
                  strokeStyle: isHidden ? mutedTextColor : backgroundColor,
                  fontColor: textColor,
                  hidden: isHidden,
                  index: i
                };
              });
            }
            return [];
          }
        }
      },
      tooltip: {
        backgroundColor: isDarkMode ? "rgba(0, 0, 0, 0.8)" : "rgba(255, 255, 255, 0.9)",
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: isDarkMode ? "#404040" : "#e5e5e5",
        borderWidth: 1,
        callbacks: {
          label: function(context: any) {
            // Use the sorted datasets for tooltip data
            const sortedDatasets = [...adjustedSampledDatasets].sort(
              (a, b) => b.sampledCount - a.sampledCount
            );
            const dataset = sortedDatasets[context.dataIndex];
            const countDisplay = dataset.sampleType === "session" ? 
              `${dataset.sampledCount}*` : `${dataset.sampledCount}`;
            return `${dataset.sampledPercentage}% (${countDisplay} requests)`;
          }
        }
      }
    }
  }), [hideLabel, hiddenDatasets, textColor, mutedTextColor, isDarkMode, adjustedSampledDatasets]);

  return (
    <div className={cn("w-full", hideLabel ? "h-full" : "h-[300px]")}>
      {selectedDatasets.length > 0 ? (
          <Pie data={chartData} options={options} />
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <div className="space-y-3">
            <PercentIcon className="w-12 h-12 text-muted-foreground mx-auto" />
            <h3 className="text-lg font-gc-med text-foreground">
              No data to visualize
            </h3>
            <p className="text-muted-foreground">
              Add datasets to see the distribution
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Main CompositionDetail Component
interface CompositionDetailProps {
  availableDatasets: Dataset[];
  selectedDatasets: SelectedDataset[];
  datasetsLoading: boolean;
  onAddDataset: (dataset: Dataset) => void;
  onRemoveDataset: (datasetId: number) => void;
  onConfigureDataset: (dataset: SelectedDataset) => void;
}

const CompositionDetail: React.FC<CompositionDetailProps> = ({
  availableDatasets,
  selectedDatasets,
  datasetsLoading,
  onAddDataset,
  onRemoveDataset,
  onConfigureDataset,
}) => {
  const compositionCardRef = useRef<HTMLDivElement>(null);
  const percentageCardRef = useRef<HTMLDivElement>(null);
  const [availableDatasetsHeight, setAvailableDatasetsHeight] = useState<string>("auto");

  // Update available datasets card height to match the combined height of composition and percentage cards
  useEffect(() => {
    const updateHeight = () => {
      if (compositionCardRef.current && percentageCardRef.current) {
        const compositionHeight = compositionCardRef.current.offsetHeight;
        const percentageHeight = percentageCardRef.current.offsetHeight;
        const totalHeight = compositionHeight + percentageHeight + 32; // 32px for the gap between cards
        setAvailableDatasetsHeight(`${totalHeight}px`);
      }
    };

    updateHeight();
    // Add resize listener to handle window resizing
    window.addEventListener("resize", updateHeight);
    
    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, [selectedDatasets]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="space-y-8">
        <div className="grid grid-cols-12 gap-8">
          {/* Available Datasets */}
          <div className="col-span-5">
            <Card 
              className="border-border" 
              style={{ height: availableDatasetsHeight, minHeight: "500px" }}
            >
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center justify-between text-lg font-gc-med text-foreground">
                  <div className="flex items-center gap-2">
                    <DatabaseIcon className="w-5 h-5 text-maitai-green" />
                    Available Datasets
                  </div>
                  {datasetsLoading && <Loader2Icon className="w-4 h-4 animate-spin" />}
                </CardTitle>
              </CardHeader>
              <CardContent className="h-full overflow-y-auto pb-6">
                {datasetsLoading ? (
                  <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="p-3 border border-border rounded">
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-24" />
                          <Skeleton className="h-3 w-full" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : availableDatasets.length > 0 ? (
                  availableDatasets.map((dataset) => (
                    <DatasetCard
                      key={dataset.id}
                      dataset={dataset}
                      isInComposition={selectedDatasets.some(
                        (item) => item.dataset.id === dataset.id
                      )}
                    />
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="space-y-3">
                      <DatabaseIcon className="w-12 h-12 text-muted-foreground mx-auto" />
                      <h3 className="text-lg font-gc-med text-foreground">
                        No datasets available
                      </h3>
                      <p className="text-muted-foreground">
                        No datasets match your current selection criteria
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Composition and Distribution */}
          <div className="col-span-7 space-y-8">
            {/* Composition Builder */}
            <Card className="border-border" ref={compositionCardRef}>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-gc-med text-foreground">
                  Composition
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CompositionBuilder
                  datasets={availableDatasets}
                  selectedDatasets={selectedDatasets}
                  onAddDataset={onAddDataset}
                  onRemoveDataset={onRemoveDataset}
                  onConfigureDataset={onConfigureDataset}
                />
                
                <div className="border-t border-border pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-muted-foreground">
                      Requests: <span className="font-gc-med text-foreground">{formatTotalRequestCount(selectedDatasets)}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Selected datasets: <span className="font-gc-med text-foreground">{selectedDatasets.length}</span>
                    </div>
                  </div>
                  {hasSessionSampling(selectedDatasets) && (
                    <div className="text-xs text-muted-foreground mt-1">
                      * Estimated count for session-based sampling
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Distribution Chart */}
            <Card className="border-border" ref={percentageCardRef}>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                  <PercentIcon className="w-5 h-5 text-maitai-green" />
                  Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PercentageVisualizer selectedDatasets={selectedDatasets} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

export {
  CompositionDetail,
  calculateSampledRequestCount,
  formatSampledRequestCount,
  hasSessionSampling,
  formatTotalRequestCount,
  PercentageVisualizer
};

export type {
  Dataset,
  SelectedDataset
}; 